import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "../../../actions";
import {
  TOGGLEUSER_RESPONSE,
  UPDATEUSERBILLING_RESPONSE,
} from "../../../constants/action_types";
import { DataTable } from "../../../components/data-table";
import { ToggleUserStatus } from "../components/user_toggle_action";
import { ToggleUserBilling } from "../components/user_billing_toggle_action";
import { LoginAsUser } from "../components/login_as_user";

function UserNameCell({ data }) {
  return (
    <div className="!w-auto !overflow-visible !whitespace-normal !text-clip">
      <div>
        {data.first_name + " " + data.last_name}
        {data.is_deleted ? " (Deleted)" : ""}
      </div>
      <div className="mt-0.5 flex flex-wrap gap-1">
        {!data.is_billable && (
          <span className="inline-block rounded bg-amber-100 px-1.5 py-[1px] text-[10px] leading-tight font-semibold text-amber-600">
            NON-BILLABLE
          </span>
        )}
        {data.license_type && (
          <span
            className={`inline-block rounded px-1.5 py-[1px] text-[10px] leading-tight font-semibold ${
              data.license_type === "PREMIUM"
                ? "bg-indigo-100 text-indigo-600"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {data.license_type}
          </span>
        )}
      </div>
    </div>
  );
}

function Users({ company_uid, companyDetails }) {
  const dispatch = useDispatch();

  const users = useSelector((state) => state.company.users);
  const users_stat = useSelector((state) => state.company.users_stat);
  const userUpdated = useSelector((state) => state.company.update_response);
  const subscriptions = useSelector((state) => state.company.subscriptions);

  const urlParams = useRef({});

  useEffect(() => {
    dispatch(
      actions.GETCOMPANYUSERSTAT_REQ({
        company_uid,
      })
    );
  }, [company_uid]);

  useEffect(() => {
    if (company_uid) {
      dispatch(actions.GETSUBSCRIPTIONS_REQ({ company_uid }));
    }
  }, [company_uid]);

  const baseAddonId = subscriptions?.plan_base_addon_mapping?.[subscriptions?.plan_id];
  const canMarkAsBase = !!baseAddonId &&
    (subscriptions?.addons || []).some((addon) => addon.item_price_id === baseAddonId);

  useEffect(() => {
    if (
      userUpdated?.type === TOGGLEUSER_RESPONSE ||
      userUpdated?.type === UPDATEUSERBILLING_RESPONSE
    ) {
      handleFetch();
    }
  }, [userUpdated]);

  const toggleUser = (user) => {
    dispatch(
      actions.TOGGLEUSER_REQ({
        company_uid,
        user_uid: user.user_uid,
        fullName: user.first_name + " " + user.last_name,
        type: user.is_active ? "DEACTIVATE" : "ACTIVATE",
      })
    );
  };

  const toggleUserBilling = (user, isBillable) => {
    dispatch(
      actions.UPDATEUSERBILLING_REQ({
        company_uid,
        user_uid: user.user_uid,
        is_billable: isBillable,
        license_type: user.license_type || "BASE",
      })
    );
  };

  const toggleUserLicenseType = (user, licenseType) => {
    dispatch(
      actions.UPDATEUSERBILLING_REQ({
        company_uid,
        user_uid: user.user_uid,
        is_billable: user.is_billable,
        license_type: licenseType,
      })
    );
  };

  const columns = [
    {
      name: "Employee Code",
      key: "emp_code",
    },
    {
      name: "Name",
      key: "first_name",
      type: "custom",
      width: "56",
      element: <UserNameCell />,
    },
    { name: "Email ", key: "email", width: "48" },
    {
      name: "Role",
      key: "role.role_name",
      formatText: (_, data) => data.role.role_name,
    },
    {
      name: "Status",
      key: "is_active",
      width: "20",
      formatText: (isActive, row) =>
        row.is_deleted ? "Deleted" : isActive ? "Active" : "Inactive",
      processClassName: (isActive, row) =>
        row.is_deleted
          ? "text-red-500 font-medium"
          : isActive
            ? "text-green-500 font-medium"
            : "text-red-500 font-medium",
    },
    { name: "Last Access Time", key: "last_access", type: "date" },
    {
      name: "Action",
      type: "actions",
      actions: [
        {
          element: <LoginAsUser companyDetails={companyDetails} />,
        },
        {
          element: <ToggleUserStatus onConfirm={toggleUser} />,
        },
        {
          element: (
            <ToggleUserBilling
              onConfirm={toggleUserBilling}
              onLicenseConfirm={toggleUserLicenseType}
              canMarkAsBase={canMarkAsBase}
            />
          ),
        },
      ],
    },
  ];
  const handleFetch = async (params) => {
    if (company_uid) {
      urlParams.current.company_uid = company_uid;
      urlParams.current.sort_by = params?.sortBy || 'created_at';
      urlParams.current.sort = params?.sort || 'ASC';
      if (params) {
        urlParams.current.page = params.page;
        urlParams.current.count = params.pageLimit;
        urlParams.current["filter.keyword"] = params.keyword;
      }
      dispatch(actions.GETCOMPANYUSER_REQ(urlParams.current));
    }
  };

  return (
    <div class="mt-3 flex h-full flex-col">
      <dl class="mb-4 grid grid-cols-2 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-4 md:divide-x md:divide-y-0">
        <div class="px-4 py-5 sm:p-6">
          <dt class="text-base font-normal text-gray-900">Total Users</dt>
          <dd class="mt-1 flex items-baseline justify-between md:block lg:flex">
            <div class="flex items-baseline text-2xl font-semibold text-indigo-600">
              {users_stat?.total}
            </div>
          </dd>
        </div>
        <div class="px-4 py-5 sm:p-6">
          <dt class="text-base font-normal text-gray-900">Active Users</dt>
          <dd class="mt-1 flex items-baseline justify-between md:block lg:flex">
            <div class="flex items-baseline text-2xl font-semibold text-indigo-600">
              {users_stat?.active}
            </div>
          </dd>
        </div>
        <div class="px-4 py-5 sm:p-6">
          <dt class="text-base font-normal text-gray-900">In-Active Users</dt>
          <dd class="mt-1 flex items-baseline justify-between md:block lg:flex">
            <div class="flex items-baseline text-2xl font-semibold text-indigo-600">
              {users_stat?.inactive}
            </div>
          </dd>
        </div>
        <div class="px-4 py-5 sm:p-6">
          <dt class="text-base font-normal text-gray-900">Non-Billable Users</dt>
          <dd class="mt-1 flex items-baseline justify-between md:block lg:flex">
            <div class="flex items-baseline text-2xl font-semibold text-amber-500">
              {users_stat?.non_billable || 0}
            </div>
          </dd>
        </div>
      </dl>
      <div class="relative w-full overflow-hidden bg-white flex-1 rounded-lg shadow px-4">
        <div class="p-2 h-full ">
          <div class="flow-root h-full ">
            <div class="h-full  -mx-4 -my-2 overflow-x-auto overflow-y-clip sm:-mx-6 lg:-mx-8">
              <div class="h-full inline-block min-w-full align-middle sm:px-6 lg:px-8">
                <DataTable
                  name="Users"
                  columns={columns}
                  layout="fixed"
                  remote={true}
                  data={users?.data}
                  totalCount={users?.total_records}
                  handleFetch={(params) => {
                    handleFetch(params);
                  }}
                  toolbar={{
                    search: { enable: true },
                  }}
                  footer={{ paginator: { enable: true, limits: [10] } }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Users;
